import React, { useState, useEffect } from "react";
import Collapsible from "components/collapsible";
import { useFilters } from "hooks/useStore";
import { mapActiveFilters, filterProducts } from "utils/filter-sort";
import { normalizeFilterParam } from "helpers/url-params";
import clsx from "clsx/lite";

const CollectionFilters = ({ data, handle, products }) => {
	if (!data?.length) return null;
	const [activeKeys, setActiveKeys] = useState(Object.fromEntries(data.map((filter) => [filter?._key, true])));
	const {
		collections, toggleFilter, setTotalActiveFilters
	} = useFilters(["collections", "toggleFilter", "setTotalActiveFilters"]);
	const { tags, priceRange } = handle in collections ? collections[handle] : {};

	useEffect(() => {
		if (!tags) return;
		const mappedActiveFilters = mapActiveFilters(tags);
		const totalActiveFilters = Object.values(mappedActiveFilters).reduce((acc, curr) => acc + curr.length, 0);
		setTotalActiveFilters(handle, totalActiveFilters);

		const url = new URL(window.location);
		Object.entries(mappedActiveFilters).forEach(([key, value]) => {
			const normalizedValue = normalizeFilterParam(key);
			if (value.length)
				url.searchParams.set(normalizedValue, encodeURIComponent(value.join(",")));
			else
				url.searchParams.delete(normalizedValue);
		});
		window.history.replaceState({}, "", url);
	}, [tags]);

	const InputFilter = ({ group, filter: { tag, displayName } }) => {
		// Filter products based on the current filter tag
		const filteredProducts = filterProducts({
			products, collectionFilters: { priceRange },
			filters: { ...mapActiveFilters(tags), [group.name]: [tag] }, matchAllFilters: true
		});

		const filteredProductsByTag = filterProducts({
			products, collectionFilters: { priceRange },
			filters: { [group.name]: [tag] }
		});

		// If there are no products that match the filter tag, return null
		if (!filteredProductsByTag.length) return null;

		// Determine if the input should be inactive
		const isDisabled = !filteredProducts.length && !tags?.[group.name]?.[tag];

		return (
			<div className={clsx("flex items-center", isDisabled && "text-secondary")}>
				<input
					id={tag}
					onChange={() => toggleFilter(handle, group.name, tag)}
					onKeyDown={(e) => e.key === "Enter" && toggleFilter(handle, group.name, tag)}
					type="checkbox"
					disabled={isDisabled}
					tabIndex={activeKeys[group?._key] ? 0 : -1}
					checked={tags?.[group.name]?.[tag] || false} className="mr-2 fill-beige cursor-pointer" />
				<label className="cursor-pointer" htmlFor={tag}>{displayName} ({filteredProducts.length})</label>
			</div>
		);
	};

	const FilterGroup = ({ group }) => {
		// Filter products based on the current filter group tags
		const filteredProducts = filterProducts({
			products, collectionFilters: { priceRange }, filters: { [group.name]: group.data.map(filter => filter.tag) }
		});

		// If there are no products that match any of the filter tags, return null
		if (!filteredProducts.length) return null;

		return (
			<Collapsible className="border-t py-5 gap-0"
				title={<h4 className="font-bold">{group.name}</h4>}
				label={group.name}
				isOpen={activeKeys[group?._key]}
				setIsOpen={() => setActiveKeys(prev => ({ ...prev, [group?._key]: !prev[group?._key] }))}
				key={group?._key}
			>
				<div className="flex flex-col gap-3 pt-6">
					{group?.data?.map((filter) =>
						<InputFilter key={filter?._key} group={group} filter={filter} />)}
				</div>
			</Collapsible>
		);
	};

	return (
		<div className="w-full lg:w-10/12">
			{data.map(filterGroup => <FilterGroup key={filterGroup?._key} group={filterGroup} />)}
		</div>
	);
};

export default CollectionFilters;
